'use client';
import React, { useState, useEffect } from 'react';
import Image from 'next/image';
import { ChevronLeft, ChevronRight, Shield, Phone } from 'lucide-react';

const ADVISORY_ITEMS = [
  {
    id: 'adv-limits',
    image: '/assets/images/safe_gambling_set_limits.png',
    alt: 'BetProtect advisory banner: Set your limits before you play — blue and gold branding',
    headline: 'Set Your Limits Before You Play',
    body: 'Decide your budget and time limit before you start. Stick to it — no matter what.',
    cta: 'Learn limit-setting strategies',
    href: '/exercises',
  },
  {
    id: 'adv-stop',
    image: '/assets/images/safe_gambling_know_when_to_stop.png',
    alt: 'BetProtect advisory: Know when to stop — clock and stop hand icon on blue background',
    headline: 'Know When to Stop',
    body: 'Chasing losses is the most common trap. If you\'ve hit your limit, walk away. Tomorrow is a new day.',
    cta: 'Try the urge surfing exercise',
    href: '/exercises',
  },
  {
    id: 'adv-fun',
    image: '/assets/images/safe_gambling_fun_not_finance.png',
    alt: 'BetProtect advisory poster: Gambling should be fun, not a financial strategy — blue and gold design',
    headline: 'Fun, Not a Financial Strategy',
    body: 'Gambling is entertainment — never a way to make money or solve financial problems.',
    cta: 'Explore coping exercises',
    href: '/exercises',
  },
];

const TIPS = [
  { icon: '⏱️', text: 'Set a time limit before you start and use an alarm.' },
  { icon: '💰', text: 'Only gamble with money you can afford to lose.' },
  { icon: '🚫', text: 'Never chase losses — the odds don\'t change.' },
  { icon: '🧠', text: 'Gambling when stressed or upset leads to poor decisions.' },
  { icon: '📵', text: 'Take regular breaks — at least 10 minutes every hour.' },
  { icon: '👥', text: 'Talk to someone you trust if gambling feels out of control.' },
];

export default function SafeGamblingAdvisory() {
  const [activeSlide, setActiveSlide] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    if (isPaused) return;
    const timer = setInterval(() => {
      setActiveSlide(p => (p + 1) % ADVISORY_ITEMS?.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [isPaused]);

  const prev = () => setActiveSlide(p => (p - 1 + ADVISORY_ITEMS?.length) % ADVISORY_ITEMS?.length);
  const next = () => setActiveSlide(p => (p + 1) % ADVISORY_ITEMS?.length);
  const current = ADVISORY_ITEMS?.[activeSlide];

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-2">
        <Shield size={18} style={{ color: '#003399' }} />
        <h3 className="text-base font-700 text-foreground">Safe Gambling Guidance</h3>
      </div>
      {/* Image carousel */}
      <div
        className="relative rounded-2xl overflow-hidden"
        style={{ background: '#001a66' }}
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
      >
        <div className="relative w-full" style={{ paddingBottom: '42%' }}>
          <Image
            src={current?.image}
            alt={current?.alt}
            fill
            className="object-cover transition-opacity duration-500"
            sizes="(max-width: 768px) 100vw, 700px"
          />
          {/* Overlay */}
          <div
            className="absolute inset-0 flex flex-col justify-end p-5"
            style={{ background: 'linear-gradient(to top, rgba(0,26,102,0.92) 0%, rgba(0,26,102,0.3) 60%, transparent 100%)' }}
          >
            <h4 className="text-white font-800 text-base sm:text-lg leading-tight">{current?.headline}</h4>
            <p className="text-white/80 text-xs sm:text-sm mt-1 max-w-md">{current?.body}</p>
            <a
              href={current?.href}
              className="mt-3 inline-flex items-center gap-1.5 text-xs font-700 px-4 py-2 rounded-full self-start transition-opacity hover:opacity-90"
              style={{ background: '#E3AE54', color: '#003399' }}
            >
              {current?.cta} →
            </a>
          </div>
        </div>

        {/* Nav arrows */}
        <button
          onClick={prev}
          className="absolute left-3 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full flex items-center justify-center transition-all"
          style={{ background: 'rgba(255,255,255,0.15)', color: '#fff' }}
          aria-label="Previous advisory"
        >
          <ChevronLeft size={16} />
        </button>
        <button
          onClick={next}
          className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full flex items-center justify-center transition-all"
          style={{ background: 'rgba(255,255,255,0.15)', color: '#fff' }}
          aria-label="Next advisory"
        >
          <ChevronRight size={16} />
        </button>

        {/* Dots */}
        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
          {ADVISORY_ITEMS?.map((_, i) => (
            <button
              key={i}
              onClick={() => setActiveSlide(i)}
              className="rounded-full transition-all duration-300"
              style={{ width: i === activeSlide ? 20 : 6, height: 6, background: i === activeSlide ? '#E3AE54' : 'rgba(255,255,255,0.4)' }}
              aria-label={`Go to slide ${i + 1}`}
            />
          ))}
        </div>
      </div>
      {/* Quick tips grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {TIPS?.map((tip, i) => (
          <div
            key={i}
            className="rounded-xl p-3 flex items-start gap-2"
            style={{ background: 'rgba(0,51,153,0.05)', border: '1px solid rgba(0,51,153,0.1)' }}
          >
            <span className="text-base flex-shrink-0">{tip?.icon}</span>
            <p className="text-xs text-foreground leading-relaxed">{tip?.text}</p>
          </div>
        ))}
      </div>
      {/* Helpline */}
      <div
        className="flex items-center gap-3 rounded-xl p-4"
        style={{ background: 'linear-gradient(135deg, #003399 0%, #0044cc 100%)' }}
      >
        <div className="w-9 h-9 rounded-full bg-white/15 flex items-center justify-center flex-shrink-0">
          <Phone size={16} className="text-white" />
        </div>
        <div className="flex-1">
          <p className="text-white font-700 text-sm">Rwanda Gambling Helpline</p>
          <p className="text-white/75 text-xs mt-0.5">Free, confidential support available 24/7</p>
        </div>
        <a
          href="tel:3029"
          className="flex-shrink-0 px-4 py-2 rounded-full text-xs font-700 transition-opacity hover:opacity-90"
          style={{ background: '#E3AE54', color: '#003399' }}
        >
          Call 3029
        </a>
      </div>
    </div>
  );
}
